import {
    EuiButtonEmpty,
    EuiButtonIcon,
    EuiDatePicker,
    EuiDatePickerRange,
    EuiFieldSearch,
    EuiFlexGroup,
    EuiFlexItem,
    EuiFlyout,
    EuiFlyoutBody,
    EuiFlyoutHeader,
    EuiPanel,
    EuiSpacer,
    EuiTitle,
} from '@elastic/eui'
import { useCallback, useEffect, useState } from 'react'

import { DEBOUNCE_DELAY } from '@utils/globals'
import { DEFAULT_DATE_CULTURE } from '@utils/dates'
import { Moment } from 'moment'
import { SelectionBox } from '@components/form'
import { useDebounce, useScreenSize } from '@hooks/utils'
import { useTranslation } from 'react-i18next'

type TableFiltersProps = {
    filters: {
        field: string
        value: string
        type: 'boolean' | 'text' | 'options' | 'date'
        placeholder?: string
        options?: any[]
    }[]
    onFilter?: (filters: string[]) => void
    onClear?: () => void
}

type FilterValue = {
    field: string
    value?: any
}

const InnerTableFilters = (props: TableFiltersProps) => {
    const [filtersValues, setFiltersValues] = useState<FilterValue[]>([])
    const debounce = useDebounce(filtersValues, DEBOUNCE_DELAY)
    const { t } = useTranslation(['common'])
    const { isMobile } = useScreenSize()
    const handleOnChange = (field: string, value: any) => {
        const newFiltersValues = [...filtersValues]
        const index = newFiltersValues.findIndex((f) => f.field === field)

        if (index === -1) {
            newFiltersValues.push({ field, value })
        } else {
            newFiltersValues[index] = { field, value }
        }

        setFiltersValues(newFiltersValues)
    }

    const handleOnClear = () => {
        setFiltersValues([])
        if (props.onClear) {
            props.onClear()
        }
    }

    const handleOnFilter = () => {
        if (props.onFilter) {
            const filterAsStrings = filtersValues.map((f) => {
                const filterDef = props.filters.find(
                    (fd) => fd.field === f.field
                )

                if (!filterDef) {
                    return ''
                }
                // Options or boolean filter string
                if (
                    filterDef.type === 'options' ||
                    filterDef.type === 'boolean'
                ) {
                    const selectedValue = f.value[0]?.value
                    if (selectedValue) {
                        return `${f.field}==${selectedValue}`
                    }
                }

                // Text filter string (contains)
                else if (filterDef.type === 'text') {
                    return `${f.field}@=${f.value}`
                }

                // Date filter string (between)
                else if (filterDef.type === 'date') {
                    const dateFrom = f.value[0]?.toDate().toISOString()
                    const dateTo = f.value[1]?.toDate().toISOString()
                    if (dateFrom && dateTo) {
                        return `${f.field}>=${dateFrom},${f.field}<=${dateTo}`
                    }
                }

                return ''
            })

            if (filterAsStrings.length > 0) {
                props.onFilter(filterAsStrings)
            }
        }
    }

    const getValue = useCallback(
        (field: string) => filtersValues.find((f) => f.field === field)?.value,
        [filtersValues]
    )

    useEffect(() => {
        if (!isMobile) {
            handleOnFilter()
        }
    }, [debounce])

    return (
        <>
            <EuiFlexGroup
                wrap
                gutterSize={isMobile ? 'm' : 's'}
                alignItems={!isMobile ? 'center' : undefined}
                direction={isMobile ? 'column' : 'row'}
                responsive={false}
            >
                {props.filters.map((filter) => (
                    <EuiFlexItem grow={isMobile} key={filter.field}>
                        {filter.type === 'text' && (
                            <EuiFieldSearch
                                placeholder={filter.placeholder}
                                value={getValue(filter.field) ?? ''}
                                compressed={!isMobile}
                                fullWidth={isMobile}
                                onChange={(e) =>
                                    handleOnChange(filter.field, e.target.value)
                                }
                            />
                        )}
                        {(filter.type === 'options' ||
                            filter.type === 'boolean') && (
                            <SelectionBox
                                options={filter.options ?? []}
                                key={filter.field}
                                style={{ minWidth: '200px' }}
                                fullWidth={isMobile}
                                placeholder={filter.placeholder}
                                compressed={!isMobile}
                                optionsAsValue={getValue(filter.field)}
                                onChange={(value) =>
                                    handleOnChange(filter.field, value)
                                }
                            />
                        )}

                        {filter.type === 'date' && (
                            <EuiDatePickerRange
                                key={filter.field}
                                startDateControl={
                                    <EuiDatePicker
                                        locale={DEFAULT_DATE_CULTURE}
                                        placeholder={filter.placeholder}
                                        selected={getValue(filter.field)?.[0]}
                                        startDate={getValue(filter.field)?.[0]}
                                        endDate={getValue(filter.field)?.[1]}
                                        compressed={!isMobile}
                                        onChange={(date: Moment) => {
                                            const value = getValue(filter.field)
                                            handleOnChange(filter.field, [
                                                date,
                                                value?.[1],
                                            ])
                                        }}
                                        showTimeSelect
                                    />
                                }
                                endDateControl={
                                    <EuiDatePicker
                                        locale={DEFAULT_DATE_CULTURE}
                                        placeholder={filter.placeholder}
                                        selected={getValue(filter.field)?.[1]}
                                        startDate={getValue(filter.field)?.[0]}
                                        endDate={getValue(filter.field)?.[1]}
                                        compressed={!isMobile}
                                        onChange={(date: Moment) => {
                                            const value = getValue(filter.field)
                                            handleOnChange(filter.field, [
                                                value?.[0],
                                                date,
                                            ])
                                        }}
                                        showTimeSelect
                                    />
                                }
                            />
                        )}
                    </EuiFlexItem>
                ))}

                <EuiFlexItem grow={isMobile}>
                    <EuiFlexGroup
                        alignItems={!isMobile ? 'center' : 'flexEnd'}
                        justifyContent={isMobile ? 'flexEnd' : undefined}
                        gutterSize={isMobile ? 'm' : 's'}
                        responsive={false}
                    >
                        <EuiFlexItem grow={false}>
                            {isMobile ? (
                                <EuiButtonEmpty
                                    iconType="search"
                                    onClick={handleOnFilter}
                                >
                                    {t('common:apply')}
                                </EuiButtonEmpty>
                            ) : (
                                <EuiButtonIcon
                                    aria-label="Filter"
                                    iconType="search"
                                    size="s"
                                    onClick={handleOnFilter}
                                />
                            )}
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            {isMobile ? (
                                <EuiButtonEmpty
                                    iconType="cross"
                                    color="text"
                                    onClick={handleOnClear}
                                >
                                    {t('common:clear_filters')}
                                </EuiButtonEmpty>
                            ) : (
                                <EuiButtonIcon
                                    aria-label="Clear filters"
                                    iconType="cross"
                                    color="text"
                                    size="s"
                                    onClick={handleOnClear}
                                />
                            )}
                        </EuiFlexItem>
                    </EuiFlexGroup>
                </EuiFlexItem>
            </EuiFlexGroup>
        </>
    )
}

const TableFilters = (props: TableFiltersProps) => {
    const { isMobile } = useScreenSize()
    const { t } = useTranslation(['common'])

    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)

    const onClear = () => {
        if (isMobile) {
            setIsMobileFiltersOpen(false)
        }

        if (props.onClear) {
            props.onClear()
        }
    }

    const onFilter = (filters: string[]) => {
        if (isMobile) {
            setIsMobileFiltersOpen(false)
        }

        if (props.onFilter) {
            props.onFilter(filters)
        }
    }

    const innerProps = {
        ...props,
        onFilter,
        onClear,
    }

    return (
        <>
            {isMobile && (
                <>
                    {isMobileFiltersOpen && (
                        <EuiFlyout
                            onClose={() => setIsMobileFiltersOpen(false)}
                        >
                            <EuiFlyoutHeader>
                                <EuiTitle>
                                    <h1>{t('common:filters')}</h1>
                                </EuiTitle>
                            </EuiFlyoutHeader>
                            <EuiFlyoutBody>
                                <InnerTableFilters {...innerProps} />
                            </EuiFlyoutBody>
                        </EuiFlyout>
                    )}
                    <EuiButtonEmpty
                        iconType="filter"
                        onClick={() => setIsMobileFiltersOpen(true)}
                    >
                        {t('common:filters')}
                    </EuiButtonEmpty>
                </>
            )}
            {!isMobile && <InnerTableFilters {...innerProps} />}
        </>
    )
}

export default TableFilters
